import React, { useState, useEffect } from 'react';
import { FaEnvelope, FaClock, FaCheckCircle, FaChartBar } from 'react-icons/fa';
import AdminStatsCard from '../components/AdminStatsCard';
import ScrapperPanel from '../components/ScrapperPanel';
import FilterPanel from '../components/FilterPanel';
import EmailTable from '../components/EmailTable';

const PlayStoreEmails = () => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    contacted: 0,
    responded: 0,
    closed: 0,
    unsubscribed: 0,
    emailSent: 0,
    recentEmails: 0
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pages: 1,
    total: 0,
    limit: 20
  });
  const [filters, setFilters] = useState({
    status: '',
    search: ''
  });
  const [scrapperQuery, setScrapperQuery] = useState('');
  const [scrapperCategory, setScrapperCategory] = useState('');
  const [scrapperMaxResults, setScrapperMaxResults] = useState(20);
  const [scrapperSort, setScrapperSort] = useState('newest');
  const [isScraping, setIsScraping] = useState(false);
  const [showScrapper, setShowScrapper] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [scrapingProgress, setScrapingProgress] = useState({
    currentApp: '',
    foundEmails: 0,
    targetEmails: 0
  });

  useEffect(() => {
    fetchEmails();
  }, [filters, pagination.current]);

  const fetchEmails = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page: pagination.current,
        limit: pagination.limit,
        ...filters
      });

      const response = await fetch(`/api/playstore-emails?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setEmails(data.data.emails);
        if (data.data.pagination) {
          setPagination({
            current: data.data.pagination.current || 1,
            pages: data.data.pagination.pages || 1,
            total: data.data.pagination.total || 0,
            limit: data.data.pagination.limit || 20
          });
        }
        setStats(data.data.stats);
      }
    } catch (error) {
      console.error('Error fetching emails:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartScraping = async () => {
    if (!scrapperQuery.trim()) return;

    setIsScraping(true);
    setScrapingProgress({
      currentApp: '',
      foundEmails: 0,
      targetEmails: scrapperMaxResults
    });
    
    // Progress simulation without app names
    const progressInterval = setInterval(() => {
      setScrapingProgress(prev => {
        const newFoundEmails = Math.min(prev.foundEmails + Math.floor(Math.random() * 2) + 1, prev.targetEmails);
        
        return {
          ...prev,
          currentApp: '', // No app name needed
          foundEmails: newFoundEmails
        };
      });
    }, 3000);
    
    try {
      const response = await fetch('/api/playstore-scrapper/scrape', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          searchQuery: scrapperQuery,
          category: scrapperCategory,
          maxResults: scrapperMaxResults,
          sort: scrapperSort
        })
      });

      const data = await response.json();
      
      if (data.success) {
        // Update progress with final data from backend
        if (data.data.progress) {
          setScrapingProgress({
            currentApp: data.data.progress.currentApp || '',
            foundEmails: data.data.progress.foundEmails || data.data.totalFound,
            targetEmails: data.data.progress.targetEmails || scrapperMaxResults
          });
        }
        
        addNotification(`Email toplama tamamlandı! ${data.data.newEmails} yeni email eklendi, ${data.data.duplicates} duplicate atlandı.`, 'success');
        fetchEmails(); // Refresh the list
      } else {
        addNotification(`Scraping error: ${data.message}`, 'error');
      }
    } catch (error) {
      console.error('Scraping error:', error);
      addNotification('Scraping error: ' + error.message, 'error');
    } finally {
      clearInterval(progressInterval);
      setIsScraping(false);
      setScrapingProgress({
        currentApp: '',
        foundEmails: 0,
        targetEmails: 0
      });
    }
  };

  const handleStatusChange = async (emailId, newStatus) => {
    try {
      const response = await fetch(`/api/playstore-emails/${emailId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        setEmails(emails.map(email => 
          email._id === emailId ? { ...email, status: newStatus } : email
        ));
        addNotification('Durum güncellendi!', 'success');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      addNotification('Durum güncellenirken hata oluştu!', 'error');
    }
  };

  const handleDeleteEmail = async (emailId) => {
    if (!window.confirm('Bu email kaydını silmek istediğinizden emin misiniz?')) return;

    try {
      const response = await fetch(`/api/playstore-emails/${emailId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        setEmails(emails.filter(email => email._id !== emailId));
        addNotification('Email kaydı silindi!', 'success');
        fetchEmails(); // Refresh stats
      }
    } catch (error) {
      console.error('Error deleting email:', error);
      addNotification('Email silinirken hata oluştu!', 'error');
    }
  };

  const handleCopyEmails = async () => {
    const emailList = emails.map(email => email.developerEmail).join(', ');
    try {
      await navigator.clipboard.writeText(emailList);
      addNotification('Email listesi kopyalandı!', 'success');
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = emailList;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      addNotification('Email listesi kopyalandı!', 'success');
    }
  };

  const handleExportEmails = () => {
    const emailList = emails.map(email => email.developerEmail).join(', ');
    const blob = new Blob([emailList], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `playstore-emails-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    addNotification('Email listesi export edildi!', 'success');
  };

  const addNotification = (message, type = 'info') => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-900 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Notifications */}
        <div className="fixed top-4 right-4 z-50 space-y-2">
          {notifications.map(notification => (
            <div
              key={notification.id}
              className={`p-3 rounded-md shadow-lg ${
                notification.type === 'success' ? 'bg-green-600' :
                notification.type === 'error' ? 'bg-red-600' :
                'bg-blue-600'
              } text-white`}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm">{notification.message}</span>
                <button
                  onClick={() => removeNotification(notification.id)}
                  className="ml-2 text-white hover:text-gray-200"
                >
                  ×
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <AdminStatsCard
            title="Toplam Email"
            value={stats.total}
            icon={FaEnvelope}
            color="indigo"
          />
          <AdminStatsCard
            title="Bekleyen"
            value={stats.pending}
            icon={FaClock}
            color="orange"
          />
          <AdminStatsCard
            title="Email Gönderildi"
            value={stats.emailSent}
            icon={FaCheckCircle}
            color="green"
          />
          <AdminStatsCard
            title="Unsubscribed"
            value={stats.unsubscribed}
            icon={FaEnvelope}
            color="red"
          />
          <AdminStatsCard
            title="Bu Hafta"
            value={stats.recentEmails}
            icon={FaChartBar}
            color="purple"
          />
        </div>

        {/* Scrapper Panel */}
        {showScrapper && (
          <ScrapperPanel
            scrapperQuery={scrapperQuery}
            setScrapperQuery={setScrapperQuery}
            scrapperCategory={scrapperCategory}
            setScrapperCategory={setScrapperCategory}
            scrapperMaxResults={scrapperMaxResults}
            setScrapperMaxResults={setScrapperMaxResults}
            scrapperSort={scrapperSort}
            setScrapperSort={setScrapperSort}
            isScraping={isScraping}
            handleStartScraping={handleStartScraping}
            scrapingProgress={scrapingProgress}
          />
        )}

        {/* Filter Panel */}
        <FilterPanel
          filters={filters}
          setFilters={setFilters}
          handleCopyEmails={handleCopyEmails}
          handleExportEmails={handleExportEmails}
        />

        {/* Email Table */}
        <EmailTable
          emails={emails}
          loading={loading}
          pagination={pagination}
          setPagination={setPagination}
          handleStatusChange={handleStatusChange}
          handleDeleteEmail={handleDeleteEmail}
          formatDate={formatDate}
        />
      </div>
    </div>
  );
};

export default PlayStoreEmails;